import {
  attachContentLanguageHeader,
  creditsOnResponse,
  creditsOnResponseError,
} from "@repo/shared/api";
import { captureMessage, setTag } from "@sentry/react";
import { AxiosError, AxiosInstance, AxiosResponse } from "axios";
import jwt_decode, { JwtPayload } from "jwt-decode";

import { getDistinctIdSync } from "../events/mixpanelClient";
import { getMixpanelContextHeaders } from "../events/mixpanelContext";
import { safeGetItem, safeGetJSON } from "../utils/safeStorage";
import { noResponseReportKey } from "../utils/sentryNoise";
import { endUserSession } from "./privateRequests/user/logout";

// Home, not the 401'd page: reloading there would refire the request. Path rides in `next`.
const removeUserAndRedirectToLogin = () => {
  endUserSession();
  const next = window.location.pathname + window.location.search;
  window.location.href = `/?login=expired&next=${encodeURIComponent(next)}`;
};

const decodeTokenAndCheckIfValidFormat = (token: string): JwtPayload => {
  try {
    return jwt_decode<JwtPayload>(token);
  } catch {
    removeUserAndRedirectToLogin();
  }
};

const checkIfTokenIsExpired = (token: JwtPayload): any => {
  if (token.exp * 1000 < new Date().getTime()) {
    //  This means that the access token has expired. Should redirect user to login and make sure user is properly logged out
    removeUserAndRedirectToLogin();
  }
};

const generateAndSetRequestId = (config: any): string => {
  // Check if correlation ID already exists
  const existingRequestId =
    config.headers["X-Correlation-ID"] || config.headers["x-correlation-id"];

  const requestId = existingRequestId || crypto.randomUUID();
  config.headers["X-Correlation-ID"] = requestId;
  setTag("request_id", requestId);
  return requestId;
};

const setMixpanelIdHeader = (config: any): void => {
  // Analytics identity, not auth. Sent on every request (anon + logged-in) so
  // backend events stitch to the same Mixpanel distinct_id as frontend events.
  try {
    const distinctId = getDistinctIdSync();
    if (distinctId) config.headers["x-mp-id"] = distinctId;
  } catch {
    // no id yet — skip, no header this request
  }
};

export const onRequest = async (config: any): Promise<any> => {
  generateAndSetRequestId(config);
  setMixpanelIdHeader(config);

  // Acquisition/device context the backend merges into its server-side Mixpanel
  // events, so they carry the same $utm_source/$os/$device/$browser/Subscription
  // as client-side events. Sent on anon requests too (alongside the distinct_id)
  // so pre-login backend events stitch to the same user. Empty values omitted.
  // Wrapped: analytics enrichment must never break the actual request (e.g.
  // localStorage can throw when storage is disabled/full in some browsers).
  try {
    Object.assign(config.headers, getMixpanelContextHeaders());
  } catch {
    // skip context headers this request
  }

  // access_token is stored JSON-stringified (see login requests), so it's read
  // back with a JSON parse. safeGetJSON keeps that exact behavior for valid
  // tokens but returns null instead of throwing when storage is disabled or the
  // stored value is somehow corrupt — this runs on every request, so a throw
  // here would break all API calls.
  const accessToken = safeGetJSON<string>("access_token");
  if (!accessToken) return config;
  const decodedToken = decodeTokenAndCheckIfValidFormat(accessToken);
  checkIfTokenIsExpired(decodedToken);

  config.headers["Authorization"] = `Bearer ${accessToken}`;
  config.headers["Platform"] = safeGetItem("platform") || "Playstation";
  return config;
};

/*
  Credit header sync and the 402 backstop used to live here. The implementation
  now lives in @repo/shared/api — one copy for web and mobile — and is attached
  by setupInterceptorsTo below. configureApi attaches it to the shared instance;
  these are web's own instances, which it does not touch.
*/
export const onResponse = (response: AxiosResponse): AxiosResponse => response;

export const onRequestError = (error: AxiosError): Promise<AxiosError> => {
  return Promise.reject(error);
};

// Client-controlled analytics headers (x-mp-signup-source-url / x-mp-utm-source)
// can carry arbitrary URL content → PII/GDPR exposure if shipped to Sentry, and
// Authorization is a bearer token. Redact both before attaching the request
// config to an error report. x-correlation-id is safe and kept for tracing.
const SENSITIVE_HEADER = /^(x-mp-|authorization$)/i;
const sanitizeConfigForSentry = (
  config: AxiosError["config"],
): AxiosError["config"] => {
  if (!config?.headers) return config;
  const redacted: Record<string, unknown> = {};
  for (const key of Object.keys(config.headers)) {
    redacted[key] = SENSITIVE_HEADER.test(key)
      ? "[redacted]"
      : (config.headers as Record<string, unknown>)[key];
  }
  return { ...config, headers: redacted as typeof config.headers };
};

// Once per endpoint per page load: a dropped connection fails every request at
// once. Spikes of this one issue mean the API is struggling.
const reportedNoResponse = new Set<string>();
const reportNoResponse = (error: AxiosError) => {
  const key = noResponseReportKey(error, {
    online: navigator.onLine,
    hidden: document.visibilityState === "hidden",
  });
  if (!key || reportedNoResponse.has(key)) return;
  reportedNoResponse.add(key);
  // Endpoint in the message, or Dedupe drops every report after the first.
  captureMessage(`API request got no response: ${key}`, {
    level: "warning",
    fingerprint: ["api-no-response"],
    tags: {
      endpoint: key,
      error_code: error.code,
      from_interceptor: true,
    },
    extra: { ms_since_navigation: Math.round(performance.now()) },
  });
};

export const onResponseError = (
  error: AxiosError,
  redirectOn401 = true,
): Promise<AxiosError> => {
  reportNoResponse(error);
  // Log 400 errors related to player-role-id being undefined
  const url = error.config?.url || "";
  const params = error.config?.params || {};
  const userId = localStorage.getItem("uuid");
  if (error.response?.status === 500) {
    captureMessage(error?.message as string, {
      tags: {
        error_type: error?.response?.status,
        endpoint: url.split("?")[0],
        from_interceptor: true,
      },
      extra: {
        url: url,
        ...(userId && { userId: userId }),
        params: params,
        request_config: sanitizeConfigForSentry(error?.config),
        response_data: error.response?.data,
      },
    });
  }
  // Auth endpoints (login/confirm/etc) opt out: a bad-credentials 401 there must
  // surface as an error, not force-logout + redirect to /login.
  if (error.response?.status === 401 && redirectOn401) {
    removeUserAndRedirectToLogin();
  }

  return Promise.reject(error);
};

export const setupInterceptorsTo = (
  axiosInstance: AxiosInstance,
  { redirectOn401 = true }: { redirectOn401?: boolean } = {},
): AxiosInstance => {
  // Web's content requests bypass the shared instance, so the header has to be
  // attached here to reach api/privateApi/authApi.
  attachContentLanguageHeader(axiosInstance);
  axiosInstance.interceptors.request.use(onRequest, onRequestError);
  /*
    Credit bookkeeping first, so the balance is applied before the app's own
    handler runs — the 401 path redirects, and a balance read after that would
    be lost. It re-rejects, so onResponseError still sees every failure. Same
    order configureApi uses on the shared instance.
  */
  axiosInstance.interceptors.response.use(
    creditsOnResponse,
    creditsOnResponseError,
  );
  axiosInstance.interceptors.response.use(onResponse, (error: AxiosError) =>
    onResponseError(error, redirectOn401),
  );
  return axiosInstance;
};
